import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@theconversation/ui";
import AmountDetails from "components/AmountDetails";
import Analytics from "lib/Analytics";
import ContactDetails from "components/ContactDetails";
import donationsTheme from "components/donationsTheme";
import FormError from "components/FormError";
import Loading from "components/Loading";
import PaymentDetails from "components/PaymentDetails";
import PaymentRequestButton from "components/PaymentRequestButton";
import PayPalManager from "lib/PayPalManager";
import RecaptchaDisclosure from "components/Recaptcha/Disclosure";
import RecaptchaValidation from "components/Recaptcha/Validation";
import StripeManager from "lib/StripeManager";

const DonationForm = ({
  amount,
  cardNumber,
  config,
  cvv,
  email,
  errors,
  expiry,
  fieldsActive,
  frequency,
  lastName,
  loading,
  name,
  onChange,
  onSubmit,
  paymentProvider,
  payPalManager,
  recaptcha,
  stripeManager,
  submitActive,
  title,
  url,
  valid,
}) => {
  const [paymentRequest, setPaymentRequest] = useState();
  useEffect(() => {
    if (stripeManager.paymentRequest && stripeManager.paymentRequest.canMakePayment) {
      stripeManager.paymentRequest.canMakePayment().then((result) => {
        const presence = result ? "present" : "absent";
        Analytics.trackCheckoutStage(2, presence);
        if (result) {
          setPaymentRequest(stripeManager.paymentRequest);
        }
      });
    }
  }, [stripeManager.paymentRequest]);

  const suggestedAmounts = config.suggestedAmounts[frequency];
  const paymentRequestButton = paymentRequest ?
    (<PaymentRequestButton
      options={{
        paymentRequest,
        style: { paymentRequestButton: { type: "donate" } },
      }}
      onClick={stripeManager.handlePaymentRequestButtonClick}
    />) : "";

  return (
    <ThemeProvider theme={donationsTheme}>
      <Loading show={loading} />

      <form
        acceptCharset="UTF-8"
        action={url}
        className="donation"
        id="new_donation"
        method="post"
        name="donation-form"
        noValidate="noValidate"
        onSubmit={onSubmit}
      >
        <FormError error={errors.payment_error} />
        <AmountDetails
          active={fieldsActive}
          amount={amount}
          currencySign={config.currencySign}
          errors={errors}
          frequency={frequency}
          initialFrequency={config.initialFrequency}
          onChange={onChange}
          defaultAmounts={config.defaultAmounts}
          suggestedAmounts={suggestedAmounts}
        />
        {paymentRequestButton}
        <ContactDetails
          active={fieldsActive}
          email={email}
          errors={errors}
          lastName={lastName}
          name={name}
          onChange={onChange}
          title={title}
        />
        <PaymentDetails
          amount={amount}
          cardNumber={cardNumber}
          cvv={cvv}
          errors={errors}
          expiry={expiry}
          frequency={frequency}
          fieldsActive={fieldsActive}
          locale={config.locale}
          onChange={onChange}
          paymentProvider={paymentProvider}
          payPalManager={payPalManager}
          stripeManager={stripeManager}
          submitActive={submitActive}
          valid={valid}
        />
        <RecaptchaValidation
          enabled={recaptcha.enabled}
          instance={recaptcha.instance}
          locale={config.locale}
          onError={recaptcha.onError}
          onVerify={recaptcha.onVerify}
          siteKey={recaptcha.siteKey}
        />
        <RecaptchaDisclosure enabled={recaptcha.enabled} />
      </form>
    </ThemeProvider>
  );
};

export const ConfigDefaultProps = {
  defaultAmounts: { once: "10", monthly: "10" },
  locale: "en",
  suggestedAmounts: { once: ["10"], monthly: ["10"] },
};

DonationForm.defaultProps = {
  amount: undefined,
  email: undefined,
  cardNumber: false,
  config: ConfigDefaultProps,
  cvv: false,
  errors: {},
  expiry: false,
  fieldsActive: true,
  frequency: "once",
  lastName: undefined,
  loading: false,
  name: undefined,
  onChange: () => {},
  onSubmit: () => {},
  paymentProvider: "credit_card",
  payPalManager: new PayPalManager({ }),
  recaptcha: {
    enabled: false,
    instance: { current: null },
    onError: () => {},
    onVerify: () => {},
    siteKey: "site-key",
  },
  stripeManager: new StripeManager({ }),
  submitActive: true,
  title: undefined,
  url: undefined,
  valid: undefined,
};

DonationForm.propTypes = {
  amount: PropTypes.string,
  cardNumber: PropTypes.bool,
  config: PropTypes.shape({
    currencySign: PropTypes.string,
    defaultAmounts: PropTypes.shape({
      once: PropTypes.string,
      monthly: PropTypes.string,
    }),
    initialFrequency: PropTypes.oneOf(["once", "monthly"]),
    locale: PropTypes.string,
    suggestedAmounts: PropTypes.shape({
      once: PropTypes.arrayOf(PropTypes.string),
      monthly: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  cvv: PropTypes.bool,
  email: PropTypes.string,
  errors: PropTypes.shape({
    amount: PropTypes.string,
    cardNumber: PropTypes.string,
    cvv: PropTypes.string,
    email: PropTypes.string,
    expiry: PropTypes.string,
    frequency: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    payment_error: PropTypes.string,
    paypal: PropTypes.string,
    title: PropTypes.string,
  }),
  expiry: PropTypes.bool,
  fieldsActive: PropTypes.bool,
  frequency: PropTypes.oneOf(["once", "monthly"]),
  lastName: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  paymentProvider: PropTypes.oneOf(["credit_card", "paypal", "wallet"]),
  payPalManager: PropTypes.instanceOf(PayPalManager),
  recaptcha: PropTypes.shape({
    enabled: PropTypes.bool,
    instance: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.elementType }),
    ]),
    onError: PropTypes.func,
    onVerify: PropTypes.func,
    siteKey: PropTypes.string,
  }),
  stripeManager: PropTypes.instanceOf(StripeManager),
  submitActive: PropTypes.bool,
  title: PropTypes.string,
  url: PropTypes.string,
  valid: PropTypes.bool,
};

export default DonationForm;
